import { useTranslation } from "react-i18next";
import type {
  ClientResponse,
  EmployeeResponse,
  MeetingStatus,
  MeetingTypeResponse,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface MeetingsFilterValues {
  since: string;
  until: string;
  client_id: string;
  meeting_type_id: string;
  /**
   * T1 / FR-26: filter the registry by Employee.id present in
   * `meeting.participants[]`. Empty string = no filter.
   */
  participant_id: string;
  status: string;
}

export interface MeetingsFiltersProps {
  draft: MeetingsFilterValues;
  onChange: (next: MeetingsFilterValues) => void;
  onApply: () => void;
  clients: ClientResponse[];
  types: MeetingTypeResponse[];
  employees: EmployeeResponse[];
}

const STATUSES: MeetingStatus[] = [
  "pending",
  "processing",
  "done",
  "partial",
  "error",
];

export function MeetingsFilters({
  draft,
  onChange,
  onApply,
  clients,
  types,
  employees,
}: MeetingsFiltersProps) {
  const { t } = useTranslation();
  return (
    <form
      // Six controls + apply button — three columns at md, six at xl.
      className="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-6"
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
      data-testid="meetings-filters"
    >
      <Input
        type="date"
        aria-label={t("meetings.filters.dateFrom")}
        value={draft.since}
        onChange={(e) => onChange({ ...draft, since: e.target.value })}
      />
      <Input
        type="date"
        aria-label={t("meetings.filters.dateTo")}
        value={draft.until}
        onChange={(e) => onChange({ ...draft, until: e.target.value })}
      />
      <select
        aria-label={t("meetings.filters.client")}
        className="h-9 rounded-md border bg-background px-2 text-sm"
        value={draft.client_id}
        onChange={(e) => onChange({ ...draft, client_id: e.target.value })}
      >
        <option value="">{t("meetings.filters.anyClient")}</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select
        aria-label={t("meetings.filters.meetingType")}
        className="h-9 rounded-md border bg-background px-2 text-sm"
        value={draft.meeting_type_id}
        onChange={(e) =>
          onChange({ ...draft, meeting_type_id: e.target.value })
        }
      >
        <option value="">{t("meetings.filters.anyMeetingType")}</option>
        {types.map((mt) => (
          <option key={mt.id} value={mt.id}>
            {mt.name}
          </option>
        ))}
      </select>
      <select
        aria-label={t("meetings.filters.participant")}
        className="h-9 rounded-md border bg-background px-2 text-sm"
        value={draft.participant_id}
        onChange={(e) => onChange({ ...draft, participant_id: e.target.value })}
      >
        <option value="">{t("meetings.filters.anyParticipant")}</option>
        {employees.map((emp) => (
          <option key={emp.id} value={emp.id}>
            {emp.full_name}
          </option>
        ))}
      </select>
      <div className="flex gap-2">
        <select
          aria-label={t("meetings.filters.status")}
          className="h-9 flex-1 rounded-md border bg-background px-2 text-sm"
          value={draft.status}
          onChange={(e) => onChange({ ...draft, status: e.target.value })}
        >
          <option value="">{t("common.dash")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`meetings.card.status.${s}`)}
            </option>
          ))}
        </select>
        <Button type="submit" size="sm" variant="outline">
          {t("common.filterApply")}
        </Button>
      </div>
    </form>
  );
}
